import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Snackbar,
  Alert
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import TodoForm from './TodoForm';
import TodoList from './TodoList';
import { useTodos } from './../../hooks/useTodos';
import { useAuth } from '../../context/AuthContext';
import { NewTodo } from '../../types/todos';

const TodoApp: React.FC = () => {
  const { user, signOut } = useAuth();
  const { todos, loading, error, addTodo, toggleTodo, deleteTodo, refreshTodos } = useTodos();
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleAddTodo = async (newTodo: NewTodo) => {
    try {
      await addTodo(newTodo);
      setSnackbar({
        open: true,
        message: 'Todo added successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to add todo',
        severity: 'error'
      });
    }
  };

  const handleToggleTodo = async (id: string, isComplete: boolean) => {
    try {
      await toggleTodo(id, isComplete);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update todo',
        severity: 'error'
      });
    }
  };

  const handleDeleteTodo = async (id: string) => {
    try {
      await deleteTodo(id);
      setSnackbar({
        open: true,
        message: 'Todo deleted!',
        severity: 'info'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete todo',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleRefresh = () => {
    refreshTodos();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Todo App
          </Typography>
          <IconButton 
            color="inherit" 
            onClick={handleRefresh} 
            disabled={loading}
            aria-label="refresh"
          >
            <RefreshIcon />
          </IconButton>
          <IconButton 
            color="inherit" 
            onClick={signOut}
            aria-label="sign out" 
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            My Todos
          </Typography>
          {user && (
            <Typography 
              variant="subtitle1" 
              color="textSecondary" 
              align="center"
              gutterBottom
            >
              Welcome, {user.email}
            </Typography>
          )}
        </Box>

        <Paper 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            boxShadow: 2
          }}
        >
          <TodoForm onAddTodo={handleAddTodo} />
          <TodoList
            todos={todos}
            loading={loading}
            error={error}
            onToggleTodo={handleToggleTodo}
            onDeleteTodo={handleDeleteTodo}
          />
        </Paper>
      </Container>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TodoApp;